import { clearAppAccessCache } from "@skypointcloud/skypoint-ui";
import { MsalProvider } from "@azure/msal-react";
import type { PublicClientApplication } from "@azure/msal-browser";
import type { SignOutBody } from "@shared/types";
import { navigate } from "wouter/use-browser-location";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  apiRequest,
  setIdleExpiredHandler,
  setSessionExpiredHandler,
  setTokenGetter,
} from "../api";
import { beginUserAction } from "../correlation";
import { forgetInstanceChoice } from "../instanceChoice";
import { DEFAULT_IDLE_TIMEOUT_MINUTES, IS_DEV_AUTH } from "../env";
import { logger } from "../logger";
import { queryClient } from "../queryClient";
import { clearAutoSignIn, markSessionExpired, markSignedOut } from "./autoSignIn";
import { DevTokenProvider } from "./devTokenProvider";
import { getMsalApp, MsalTokenProvider } from "./msalTokenProvider";
import { takeRedirectRefusal } from "./redirectRefusal";
import { isMsalError, signInErrorCode } from "./signInErrors";
import { clearReauthHandoff } from "./reauthHandoff";
import {
  clearLastActivity,
  IDLE_LOCK_KEY,
  idleLocked,
  markSessionEnded,
  readLastActivity,
  readSessionEnded,
  sessionDeadlinePassed,
  setIdleLock,
  setSessionPolicy,
  openSessionChannel,
  type SessionChannel,
} from "./sessionSync";
import type {
  AuthAccount,
  AuthProviderOption,
  ReauthOutcome,
  SignInOptions,
  TokenProvider,
} from "./types";

/**
 * Why a session ended — audited as `auth.sign_out` metadata (SEC-14). The
 * server's `signOutBodySchema` is a closed enum, so this stays in step with it
 * rather than inventing a third reason the route would reject.
 */
export type SignOutReason = SignOutBody["reason"];

export type AuthStatus = "initializing" | "signed-out" | "signed-in" | "failed";

interface AuthContextValue {
  status: AuthStatus;
  account: AuthAccount | null;
  kind: TokenProvider["kind"];
  /**
   * The ways to sign in (workforce SSO): one per configured provider, empty for the dev
   * provider and before start-up. The sign-in page draws a button per entry.
   */
  providers: readonly AuthProviderOption[];
  /** Where "Contact Support" points; undefined means the page's default. */
  supportUrl: string | undefined;
  /** The provider this tab started on; undefined for the dev provider and the single form. */
  activeProviderId: string | undefined;
  error: Error | null;
  signIn: (hint?: string, options?: SignInOptions) => Promise<void>;
  signOut: (reason?: SignOutReason) => Promise<void>;
  /**
   * End the session in this tab only, without an audit row or a broadcast: another tab already
   * ended it and said so (B-18). Used by the idle clock when it finds the session ended elsewhere.
   */
  endSessionHere: (reason: SignOutReason) => Promise<void>;
  /** SEC-14 automatic logoff in minutes: the server's, else the 30-minute default (fix F). */
  idleTimeoutMinutes: number;
  getAccessToken: () => Promise<string | null>;
  /** FR-14: prove the same identity again for a privileged write. */
  reauthenticate: () => Promise<ReauthOutcome>;
  /**
   * Start again after `failed`: the sign-in configuration or MSAL could not be
   * loaded (Session 9 contract §2.5). The failed start-up is not cached, so this
   * fetches the configuration again.
   */
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** One empty list, so a provider without options does not change the context on every render. */
const NO_PROVIDERS: readonly AuthProviderOption[] = [];

/** How long the sign-out audit call may take before the sign-out goes on without it. */
const SIGN_OUT_AUDIT_TIMEOUT_MS = 5_000;

/**
 * #2006: the refusal the identity provider sent back while nobody is signed in, logged by its
 * code. The page shows its sentence beside every way to sign in, and nothing redirects again.
 */
function redirectRefusalToShow(): Error | null {
  const refusal = takeRedirectRefusal();
  if (refusal) {
    logger.warn("sign-in redirect came back refused", {
      "skydata.reason": signInErrorCode(refusal) ?? "unknown",
    });
  }
  return refusal;
}

/**
 * A start-up that failed. An MSAL refusal is logged by its code only (#2006): Microsoft's text
 * names the person's directory and the resource. The page shows `signInErrorView`'s sentence.
 */
function logBootFailure(cause: unknown): void {
  if (isMsalError(cause)) {
    logger.error("authentication could not be initialised", undefined, {
      "skydata.reason": signInErrorCode(cause) ?? "unknown",
    });
  } else {
    logger.error("authentication could not be initialised", cause);
  }
}

/**
 * Owns the session (FR-10, FR-14) and hands the fetch layer its token getter.
 *
 * The provider itself is chosen once, at mount: MSAL in a real environment, the
 * dev issuer when `VITE_DEV_AUTH=1`. Everything above this component is
 * identical either way.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [provider, setProvider] = useState<TokenProvider | null>(null);
  const [msalApp, setMsalApp] = useState<PublicClientApplication | null>(null);
  const [status, setStatus] = useState<AuthStatus>("initializing");
  const [account, setAccount] = useState<AuthAccount | null>(null);
  const [error, setError] = useState<Error | null>(null);
  // Bumped by `retry`; the boot effect runs once per value.
  const [bootAttempt, setBootAttempt] = useState(0);

  // The provider is held in a ref as well as in state: the MSAL implementation
  // reads its account from MSAL's own cache, so a change needs a nudge to React
  // rather than a second copy of the account that can disagree with it.
  const providerRef = useRef<TokenProvider | null>(null);
  // The idle-expired handler, for `getAccessToken` (defined before the handler is).
  const idleExpiredRef = useRef<(() => void) | null>(null);
  // Issue #1395: set when this document starts ending its session (a sign-out or an end here),
  // cleared only when a session starts again. Ending a session clears the shared idle clock, and
  // an MSAL session reads a missing clock as an expired one, so without this a wake, a request or
  // the idle timer in the moments before the logout navigation took the ended session for an idle
  // one: a second sign-out audited as `idle` (401, its token already gone), the idle lock, and
  // "Your session ended" on the sign-in page of a person who chose to leave.
  const sessionEnding = useRef(false);
  // When this document's session began (it read signed in), so a session-ended stamp another tab
  // wrote after it can be told from one an earlier session left (#1395). Set below.
  const sessionStartedAt = useRef<number | null>(null);
  const syncFromProvider = useCallback(() => {
    const current = providerRef.current;
    if (current?.account) sessionEnding.current = false;
    setAccount(current?.account ?? null);
    setStatus(current?.account ? "signed-in" : "signed-out");
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        if (IS_DEV_AUTH) {
          const dev = new DevTokenProvider(syncFromProvider);
          if (cancelled) return;
          providerRef.current = dev;
          setProvider(dev);
          setAccount(dev.account);
          setStatus(dev.account ? "signed-in" : "signed-out");
          return;
        }
        const app = await getMsalApp();
        if (cancelled) return;
        const msal = new MsalTokenProvider(app);
        providerRef.current = msal;
        setMsalApp(app);
        setProvider(msal);
        setAccount(msal.account);
        const refusal = redirectRefusalToShow();
        if (refusal) setError(refusal);
        setStatus(msal.account ? "signed-in" : "signed-out");
      } catch (cause) {
        if (cancelled) return;
        logBootFailure(cause);
        setError(cause instanceof Error ? cause : new Error(String(cause)));
        setStatus("failed");
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [syncFromProvider, bootAttempt]);

  const retry = useCallback(() => {
    beginUserAction();
    // A failed start-up on `/callback` leaves the automatic redirect's attempt
    // marker behind; after a Retry it would show "Sign-in did not finish" for a
    // round trip that never got as far as the identity provider.
    clearAutoSignIn();
    setError(null);
    setStatus("initializing");
    setBootAttempt((attempt) => attempt + 1);
  }, []);

  /** The API layer's token: `api.ts` checks the idle deadline itself before it asks. */
  const rawAccessToken = useCallback(async () => {
    if (!provider) return null;
    return provider.getAccessToken();
  }, [provider]);

  /**
   * Every other consumer's token (the live socket, the app switcher): none after the idle
   * deadline, which ends the session instead (round 9 review #2), so nothing outside `api.ts`
   * redeems a refresh token for a session that is over.
   */
  const getAccessToken = useCallback(async () => {
    if (sessionDeadlinePassed()) {
      idleExpiredRef.current?.();
      return null;
    }
    return rawAccessToken();
  }, [rawAccessToken]);

  const signIn = useCallback(
    async (hint?: string, options?: SignInOptions) => {
      if (!provider) return;
      beginUserAction();
      // Signing in starts a session: the one that ended before it no longer holds anything off.
      sessionEnding.current = false;
      await provider.signIn(hint, options);
      // MSAL's `loginRedirect` is leaving the page: this document has no session to show.
      if (provider.kind === "msal") return;
      setAccount(provider.account);
      setStatus(provider.account ? "signed-in" : "signed-out");
      // The markers are retired by `useAutoSignIn` once `/api/v2/me` answers (B-07), not here:
      // an account is not yet a session skyData accepts.
    },
    [provider],
  );

  /**
   * FR-14, SEC-4: a privileged write asked for a fresher `auth_time`.
   *
   * Deliberately NOT routed through `signIn`: that resets the shell's idea of
   * the session and would unmount the screen holding the half-saved form. Here
   * the account does not change, so nothing above needs to re-render — only the
   * token behind `getAccessToken` is newer.
   */
  const reauthenticate = useCallback(async (): Promise<ReauthOutcome> => {
    if (!provider) return "failed";
    beginUserAction();
    return provider.reauthenticate();
  }, [provider]);

  // This tab's end of the session channel (B-18): what it posts reaches every other tab.
  const channel = useRef<SessionChannel | null>(null);

  /**
   * Everything a session leaves in this tab, cleared. Shared by a sign-out here and one another
   * tab announced, so the two can never drift apart.
   */
  const clearLocalSession = useCallback(() => {
    // The app-switcher's access cache is keyed by base URL and instance, NOT
    // by user, with a five-minute TTL — without this the next person to sign
    // in on this browser briefly sees the previous user's app list.
    clearAppAccessCache();
    // A re-authentication handoff can be holding a credential the user typed
    // and never saved (see `reauthHandoff`). The session it belonged to is
    // ending, so the staged value has nothing left to be replayed into and no
    // reason to outlive it in sessionStorage — which survives both sign-out
    // and the identity provider's navigation within the same tab.
    clearReauthHandoff();
    // Forget the instance this tab and browser were in (round 6 T-08). If it were kept, the
    // next sign-in here (another person, or this one after a switch on another device) would
    // land where this session was rather than on that person's own saved instance.
    forgetInstanceChoice();
    // The cache holds this person's rows and `/me`; the next sign-in on this page must not
    // render them, even for a moment (B-44).
    queryClient.clear();
    // The ended session's idle clock is not the next one's (review r9 #1).
    clearLastActivity();
  }, []);

  const endSessionHere = useCallback(
    async (reason: SignOutReason) => {
      if (!providerRef.current) return;
      if (sessionEnding.current) return;
      sessionEnding.current = true;
      try {
        // The same markers as a sign-out here: an idle one says why on the sign-in page, and a
        // deliberate one stays signed out.
        if (reason === "idle") {
          markSessionExpired(providerRef.current.account?.email);
          setIdleLock();
        } else markSignedOut();
        clearLocalSession();
        await providerRef.current.signOut({ local: true });
        setAccount(null);
        setStatus("signed-out");
      } catch (cause) {
        sessionEnding.current = false;
        throw cause;
      }
    },
    [clearLocalSession],
  );

  /**
   * #1395, across tabs: another tab ended this session by choice and its broadcast has not
   * arrived yet. Its sign-out cleared the shared idle clock, which a strict session reads as
   * expired, so without this a request, a wake or an idle tick here would take the ended session
   * for an idle one (an idle audit row and the idle lock over a chosen Sign out). An idle logoff
   * elsewhere sets the lock before it clears the clock, so a missing clock with no lock and a
   * session-ended stamp from this session's lifetime is a chosen sign-out in another tab.
   */
  const endedElsewhereByChoice = useCallback((): boolean => {
    const since = sessionStartedAt.current;
    if (since === null || idleLocked() || readLastActivity() !== null) return false;
    const ended = readSessionEnded();
    return ended !== null && ended >= since;
  }, []);

  const signOut = useCallback(
    async (requested: SignOutReason = "user") => {
      if (!provider) return;
      // One ending per session (#1395): whatever asks while it is under way is the same ending.
      if (sessionEnding.current) return;
      // Already ended by choice in another tab: end here as that one did, with no second row.
      if (endedElsewhereByChoice()) return endSessionHere("user");
      sessionEnding.current = true;
      // Set once this document has started taking the session down (the clock, the markers and
      // the other tabs): past it, a failure cannot leave the session as it was.
      let tornDown = false;
      let reason: SignOutReason = requested;
      try {
        beginUserAction();
        // A Sign out after the idle deadline is the idle logoff the timer had not reached yet.
        if (requested === "user" && sessionDeadlinePassed()) reason = "idle";
        // Written BEFORE anything else: between "the session is over" and the
        // identity provider's logout navigation the app is signed out on a
        // protected route, which is exactly the state that starts an automatic
        // sign-in redirect. Without this marker a sign-out races a sign-in and
        // sometimes loses (see `autoSignIn.ts`).
        //
        // An idle expiry writes its own reason: the user did not choose to leave, so `/sign-in`
        // says the session ended and its one click opens the identity provider's account picker,
        // the address that was signed in as the hint (round 9: one click, never zero).
        if (reason === "idle") markSessionExpired(provider.account?.email);
        else markSignedOut();
        // Every other tab's idle clock reads this before it signs out on its own, so one idle
        // logoff writes one audit row, not one per tab (B-18).
        markSessionEnded();
        // One click, not zero (round 9 review): an idle logoff locks every tab and every later
        // document of this browser until an interactive sign-in. The audit's token is taken first,
        // then the lock is written at once, so a slow or hung audit call never delays it; every
        // other tab hears the storage event and clears its cache too.
        let auditToken: string | null = null;
        if (reason === "idle") {
          auditToken = await provider.getAccessToken().catch(() => null);
          setIdleLock();
        }
        try {
          // Audited server-side as `auth.sign_out` with this reason (SEC-14).
          // Best effort, and bounded: a failed audit write must not strand a user in a session
          // they asked to end.
          await apiRequest<void>("/api/v2/auth/sign-out", {
            method: "POST",
            body: { reason },
            // The idle sign-out is the one call that may leave after the idle deadline, with the
            // token taken before the lock.
            allowAfterIdle: reason === "idle",
            ...(auditToken ? { headers: { authorization: `Bearer ${auditToken}` } } : {}),
            signal: AbortSignal.timeout(SIGN_OUT_AUDIT_TIMEOUT_MS),
          });
        } catch (cause) {
          logger.warn("sign-out could not be recorded", {
            "skydata.reason": reason,
            "skydata.cause": String(cause),
          });
        }
        tornDown = true;
        // One logoff ends every tab (B-18), before the identity provider's logout navigation
        // takes this one away, and before the shared idle clock is cleared, so the other tabs
        // hear "signed out" as early as they can (#1395).
        channel.current?.post({ type: "signed-out", reason });
        clearLocalSession();
        if (reason === "idle") {
          // Round 9, sign-out fix B: the idle logoff is LOCAL. This browser forgets its tokens; the
          // identity provider's session stands, so coming back is a click on the account picker,
          // not a password. Only the Sign out button ends the identity provider's session.
          await provider.signOut({ local: true });
          // What MSAL's logout page used to do: `/sign-in`, which takes the person back.
          if (provider.kind === "msal") navigate("/sign-in", { replace: true });
        } else {
          await provider.signOut();
        }
        setAccount(null);
        setStatus("signed-out");
      } catch (cause) {
        // Nothing was taken down yet: the session is as it was, so it can be asked again.
        if (!tornDown) {
          sessionEnding.current = false;
          throw cause;
        }
        // Review of #1395: past the teardown the idle clock is gone and the other tabs were told,
        // so releasing the latch would turn the next request or idle tick into an idle logoff of
        // a session the person chose to end. The identity provider's logout failed (MSAL
        // `interaction_in_progress`, say): end this browser's session locally instead, as an idle
        // logoff does, and keep the latch held until the next sign-in.
        logger.warn("sign-out could not reach the identity provider; ended locally", {
          "skydata.reason": reason,
          "skydata.cause": signInErrorCode(cause) ?? "unknown",
        });
        await provider.signOut({ local: true }).catch(() => undefined);
        setAccount(null);
        setStatus("signed-out");
        if (provider.kind === "msal") navigate("/sign-in", { replace: true });
      }
    },
    [provider, clearLocalSession, endedElsewhereByChoice, endSessionHere],
  );

  useLayoutEffect(() => {
    if (status === "signed-in") sessionStartedAt.current ??= Date.now();
    else sessionStartedAt.current = null;
  }, [status]);

  // Another tab ended the session: end it here at once, without a second audit row (B-18).
  useEffect(() => {
    if (status !== "signed-in") return;
    const open = openSessionChannel((event) => {
      if (event.type === "signed-out") void endSessionHere(event.reason);
    });
    channel.current = open;
    return () => {
      open.close();
      if (channel.current === open) channel.current = null;
    };
  }, [status, endSessionHere]);

  // Round 9 review: the policy the API layer enforces before every request (the idle deadline by
  // the wall clock, fail closed for an MSAL session), armed only while signed in.
  const kindNow = provider?.kind ?? (IS_DEV_AUTH ? "dev" : "msal");
  const idleMinutesNow = provider?.idleTimeoutMinutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES;
  useLayoutEffect(() => {
    setSessionPolicy({
      idleMs: idleMinutesNow * 60_000,
      strict: kindNow === "msal",
      enforced: status === "signed-in",
    });
  }, [idleMinutesNow, kindNow, status]);

  // A request about to leave after the deadline ends the session as an idle logoff, once. When
  // another tab already did (the lock), this one only ends here: no second audit row (review #4).
  const idleEnding = useRef(false);
  const onIdleExpired = useCallback(() => {
    if (idleEnding.current || sessionEnding.current) return;
    idleEnding.current = true;
    const ending = idleLocked() ? endSessionHere("idle") : signOut("idle");
    void ending.finally(() => {
      idleEnding.current = false;
    });
  }, [signOut, endSessionHere]);
  useLayoutEffect(() => {
    idleExpiredRef.current = onIdleExpired;
    setIdleExpiredHandler(onIdleExpired);
    return () => setIdleExpiredHandler(null);
  }, [onIdleExpired]);

  // Another tab locked the browser (its idle logoff): this one clears its tokens and ends at
  // once, even if it missed the broadcast (a storage event reaches every other document).
  useEffect(() => {
    if (status !== "signed-in") return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === IDLE_LOCK_KEY && event.newValue !== null) void endSessionHere("idle");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [status, endSessionHere]);

  // The fetch layer has no React coupling; it calls back into whatever is
  // registered here.
  // Layout effects run before any child's passive effect, and TanStack Query fetches from
  // passive effects. With a passive effect here the first /api/v2/me of a document load left
  // before the getter existed, answered 401, and the session-expired handler signed the user
  // out on every page reload.
  useLayoutEffect(() => {
    setTokenGetter(rawAccessToken);
    return () => setTokenGetter(null);
  }, [rawAccessToken]);

  useLayoutEffect(() => {
    setSessionExpiredHandler(() => {
      // A 401 after a token we believed was good: the session is gone. Drop to
      // the sign-in screen rather than looping requests. Round 7 (backlog "Forget the instance
      // choice when a session ends by a 401"): forget the instance as a sign-out does, or a
      // second account signing in in this tab would land where this session was. The same
      // person lands on their saved instance (`preferences.lastInstanceId`), which every switch
      // in this tab already wrote, so they come back to what they were working in.
      forgetInstanceChoice();
      queryClient.clear();
      setAccount(null);
      setStatus("signed-out");
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      account,
      kind: provider?.kind ?? (IS_DEV_AUTH ? "dev" : "msal"),
      providers: provider?.providers ?? NO_PROVIDERS,
      supportUrl: provider?.supportUrl,
      activeProviderId: provider?.activeProviderId,
      error,
      signIn,
      signOut,
      endSessionHere,
      idleTimeoutMinutes: provider?.idleTimeoutMinutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES,
      getAccessToken,
      reauthenticate,
      retry,
    }),
    [
      status,
      account,
      provider,
      error,
      signIn,
      signOut,
      endSessionHere,
      getAccessToken,
      reauthenticate,
      retry,
    ],
  );

  const tree = <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  // MsalProvider is mounted only when MSAL is the active provider, so the dev
  // path does not need an MSAL instance to exist at all.
  return msalApp ? <MsalProvider instance={msalApp}>{tree}</MsalProvider> : tree;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
